

import React, { useEffect, useState } from 'react';
import { Calendar, Eye, User, Search, Filter } from 'lucide-react';
import Navbar from './navbar';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { showErrorToast } from './toast';
import InfiniteScroll from 'react-infinite-scroll-component';

const BlogCard = ({ blog }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = () => {
    const statusConfig = {
      0: { text: 'Draft', bg: 'secondary' },
      1: { text: 'Published', bg: 'success' },
      2: { text: 'Archived', bg: 'warning' }
    };

    const config = statusConfig[blog.blogStatus] || statusConfig[0];
    return config;
  };

  const status = getStatusBadge();
  const navigate = useNavigate();

  return (
    <div className="card shadow-sm border-0"
      style={{
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        minHeight: '280px',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-8px)';
        e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)';
      }}>
      <div className="row">
        {/* Image Container - Left Side */}
        <div className='col-md-5'>
          <img
            src={`${process.env.REACT_APP_API_URL}${blog.thumbnailImageUrl}`}
            alt={blog.thumbnailImageAlt}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.4s ease',
              aspectRatio: '4/3',
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          />


        </div>

        {/* Content - Right Side */}
        <div className='col-md-7'>
          <div className="card-body d-flex flex-column justify-content-between">
            <div>
              <h5 className="card-title fw-bold mb-3" style={{ fontSize: '1.3rem', color: '#1a1a1a', lineHeight: '1.3' }}>
                {blog.title}
              </h5>

              {/* Meta Info - Top */}
              <div className="d-flex flex-wrap align-items-center gap-3 mb-3" style={{ fontSize: '0.85rem' }}>
                <div className="d-flex align-items-center text-muted">
                  <User size={16} className="me-2" />
                  <span>{"Dimora Living"}</span>
                </div>
                <div className="d-flex align-items-center text-muted">
                  <Calendar size={16} className="me-2" />
                  <span>{formatDate(blog.creationTime)}</span>
                </div>
                <div className="d-flex align-items-center text-muted">
                  <Eye size={16} className="me-2" />
                  <span>{blog.blogViewed?.toLocaleString() || 0} views</span>
                </div>
              </div>

              {/* Divider */}
              <div style={{ borderBottom: '1px solid #e0e0e0', margin: '12px 0' }}></div>

              <p className="card-text text-muted" style={{ fontSize: '0.95rem', lineHeight: '1.5', marginTop: '12px' }}>
                {blog.shortDesc}
              </p>
            </div>

            {/* Button */}
            <button
              className="btn"
              style={{
                backgroundColor: '#8B5A3C',
                borderColor: '#8B5A3C',
                color: 'white',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                padding: '10px 32px',
                fontSize: '0.95rem',
                marginTop: '15px',
                width: 'auto',
                alignSelf: 'flex-start'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#6d4530';
                e.currentTarget.style.borderColor = '#6d4530';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#8B5A3C';
                e.currentTarget.style.borderColor = '#8B5A3C';
              }}

              onClick={() => {
                navigate(`/blog/${blog.slug}`);
              }}
            >
              READ MORE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BlogView = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('latest');
  const [blogs, setBlogs] = useState([]);
  const [blogsPerPage] = useState(8);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const fetchBlogs = async (pageNumber = 1, isInitialLoad = false) => {
    try {
      // Use URLSearchParams to manage query params
      const params = new URLSearchParams({
        page: pageNumber,
        pageSize: blogsPerPage,
        status: 1
      });

      if (searchTerm && searchTerm.trim() !== "") {
        params.append("search", searchTerm.trim());
      }
      if (sortBy && sortBy.trim() !== "") {
        params.append("sort", sortBy.trim());
      }

      const getBlogPath = `${process.env.REACT_APP_API_URL}api/Blogs?${params.toString()}`;
      const token = localStorage.getItem("token");

      const res = await axios.get(getBlogPath, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      // const data = res.data;
      const data = res.data?.blogs || [];

      if ((data?.length < blogsPerPage) || (res.data.totalCount <= blogs?.length + data?.length)) {
        setHasMore(false);
      }

      if (data?.length > 0) {
        if (pageNumber === 1) {
          // First page - replace properties
          setBlogs(data);
        } else {
          // Subsequent pages - append properties
          setBlogs(prev => [...prev, ...data]);
        }
        setPage(prev => prev + 1);
      }

    } catch (error) {
      showErrorToast("Failed to fetch blogs. Please try again later.");
      console.error(error);
    } finally {
      setInitialLoading(false);
    }
  };

  const filteredBlogs = blogs
    .filter(blog => {
      const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.shortDesc.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch && !blog.isDeleted;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.creationTime) - new Date(a.creationTime);
      } else if (sortBy === 'oldest') {
        return new Date(a.creationTime) - new Date(b.creationTime);
      } else if (sortBy === 'popular') {
        return (b.blogViewed || 0) - (a.blogViewed || 0);
      }
      return 0;
    });


  // Custom Property Loader for InfiniteScroll
  const PropertyLoader = () => {
    return (
      <div className="row g-4 mt-0">
        {[...Array(4)].map((_, index) => (
          <PropertyCardSkeleton key={index} />
        ))}
      </div>
    );
  };


  // Skeleton Loader component for property cards
  const PropertyCardSkeleton = () => {
    return (
      <div className=" col-12">
        <div className="card property-card rounded-3 border-0 shadow overflow-hidden">
          <div className="skeleton-image"></div>
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="skeleton-text skeleton-price"></div>
              <div className="skeleton-badge"></div>
            </div>
            <div className="skeleton-text skeleton-title mb-3"></div>
            <div className="skeleton-text skeleton-address mb-3"></div>
            <div className="d-flex justify-content-between flex-wrap gap-2">
              <div className="skeleton-text skeleton-detail"></div>
              <div className="skeleton-text skeleton-detail"></div>
              <div className="skeleton-text skeleton-detail"></div>
            </div>
            <div className="d-flex mt-3 gap-2">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="skeleton-amenity"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };


  useEffect(() => {
    setBlogs([]);
    setPage(1);
    setHasMore(true);
    setInitialLoading(true);
    fetchBlogs(1, true);
  }, [searchTerm, sortBy]);

  return (
    <>
      <Navbar navClass="defaultscroll sticky" menuClass="navigation-menu nav-right" />
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />

      <div className="min-vh-100 mt-5" style={{ background: 'linear-gradient(to bottom right, #f8f9fa, #e7f1ff)' }}>
        <div className="container py-5">
          {/* Header */}
          <div className="text-center mb-5">
            <h1 className="display-4 fw-bold mb-3" style={{ color: '#1a1a1a' }}>
              Our Blog
            </h1>
            <p className="lead text-muted">
               Explore our blogs for tips, inspiration, and updates to make your apartment search easier.
            </p>
          </div>

          {/* Filters and Search */}
          <div className="card shadow-sm mb-4 border-0">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-8">
                  <div className="position-relative">
                    <Search
                      size={20}
                      className="position-absolute text-muted"
                      style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                    />
                    <input
                      type="text"
                      placeholder="Search blogs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="form-control ps-5"
                    />
                  </div>
                </div>


                <div className="col-md-4">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="form-select"
                  >
                    <option value="latest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="popular">Most Popular</option>
                  </select>
                </div>
              </div>

              <div className="mt-3 text-muted small">
                Showing {blogs.length} {blogs.length === 1 ? 'blog' : 'blogs'}
              </div>
            </div>
          </div>
          {/*Blog Grid */}
          {initialLoading ? (
            <div className="row g-4 mt-0">
              {[...Array(8)].map((_, index) => (
                <PropertyCardSkeleton key={index} />
              ))}
            </div>
          ) : (
            <InfiniteScroll
              dataLength={blogs.length}
              next={() => fetchBlogs(page)}
              hasMore={hasMore}
              loader={<PropertyLoader />}
              scrollThreshold={0.3} // Triggers next() at 30% scroll
            >
              <div>
                {blogs.length > 0 ? (
                  <div className="row" style={{rowGap: '20px'}}>
                    {blogs.map(blog => (
                      <div key={blog.id} className="col-12">
                        <BlogCard blog={blog} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted fs-5">No blogs found matching your criteria.</p>
                  </div>
                )}
              </div>
            </InfiniteScroll>
          )}

        </div>
      </div>
    </>
  );
};

export default BlogView;



